import React, { useRef, useEffect } from "react";
import styled from "styled-components";
import { useNavigate, useNavigationType } from "react-router";
import { useDispatch, useSelector } from "react-redux";
import PullToRefresh from "react-simple-pull-to-refresh";

// layouts
import { MainLayout } from "layouts";

// components
import { Button } from "components/common";
import { NotificationItem, TypeFilter } from "components/notifications";
import Observer from "components/ui/Observer";
import PullToRefreshing from "components/common/PullToRefreshing";
import Text from "components/ui/Text";
import Section from "components/ui/Section";

// redux
import {
  fetchNotifications,
  readNotifications,
  setFromTop,
} from "redux/slices/notifications/notificationsSlice";

const Notifications = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const pageRef = useRef(null);
  const scrollRef = useRef(0);
  const navigationType = useNavigationType();
  const { isLoading, filter, list, pagination, fromTop } = useSelector(
    (state) => state.notifications,
  );

  // runs only on first render
  useEffect(() => {
    if (navigationType === "PUSH" && !isLoading) {
      dispatch(fetchNotifications({ isReset: true, filter: [] })).then(() => {
        dispatch(readNotifications());
        const deviceInfo = navigator.userAgent;
        if (deviceInfo.includes("Android")) {
          window.CaremindBridge?.cleanNotification();
        }
      });
    }
    scrollHandler.restore();
  }, []);

  const handleClickFilter = ({ filter }) => {
    scrollHandler.scrollToTop();
    dispatch(fetchNotifications({ isReset: true, filter: filter }));
  };

  const HeaderComponents = {
    rightComponent: () => {
      return (
        <Button
          variant="text"
          text="설정"
          onClick={() => {
            navigate("/all/settings/notifications");
            scrollHandler.save();
          }}
        />
      );
    },

    bottomComponent: () => {
      return <TypeFilter onClick={handleClickFilter} />;
    },
  };

  const scrollHandler = {
    onScrollChange: (e) => {
      const { scrollTop } = e.target;
      scrollRef.current = scrollTop;
    },
    save: () => {
      dispatch(setFromTop(scrollRef.current));
    },
    scrollToTop: () => {
      scrollRef.current = 0;
      pageRef.current.scrollTo({
        top: 0,
      });
      dispatch(setFromTop(0));
    },
    restore: () => {
      if (fromTop > 0) {
        pageRef.current.scrollTo({
          top: fromTop,
        });
      }
    },
  };

  const handleRefresh = () => {
    return new Promise((res) => {
      setTimeout(() => {
        dispatch(fetchNotifications({ isReset: true, filter: filter })).then(
          () => {
            dispatch(readNotifications());
            const deviceInfo = navigator.userAgent;
            if (deviceInfo.includes("Android")) {
              window.CaremindBridge?.cleanNotification();
            }
          },
        );
        res();
      }, 700);
    });
  };

  return (
    <PullToRefresh
      onRefresh={handleRefresh}
      resistance={2}
      pullingContent={<></>}
      refreshingContent={<PullToRefreshing />}
    >
      <MainLayout
        withHeader={true}
        withBottomTab={false}
        backgroundColor="white"
        headerProps={{
          padding: "0 16px 0 0",
          withBackButton: true,
          centerTitle: "알림",
          rightComponent: HeaderComponents.rightComponent,
          bottomComponent: HeaderComponents.bottomComponent,
        }}
      >
        <ListWrapper ref={pageRef} onScroll={scrollHandler.onScrollChange}>
          {list.length === 0 ? (
            isLoading ? (
              <WaitingBox>
                <Section isLoading />
              </WaitingBox>
            ) : (
              <EmptyBox>
                <Text color="bluegray40" lineHeight="24px">
                  새로운 알림이 없습니다
                </Text>
              </EmptyBox>
            )
          ) : (
            <>
              {list?.map((item) => (
                <NotificationItem
                  key={item.seqManagerAlarmSendHistory}
                  isRead={item.readFlag === 1}
                  notification={item}
                  saveScrollPosition={scrollHandler.save}
                />
              ))}
              <Observer
                isActive={pagination.hasMore}
                isLoading={true}
                onVisible={() =>
                  !isLoading &&
                  dispatch(
                    fetchNotifications({ isReset: false, filter: filter }),
                  )
                }
              />
            </>
          )}
        </ListWrapper>
      </MainLayout>
    </PullToRefresh>
  );
};

const ListWrapper = styled.div`
  margin-top: 56px;
  width: 100%;
  height: calc(100% - 108px);
  overflow: scroll;
`;

const WaitingBox = styled.div`
  margin-top: 20px;
`;

const EmptyBox = styled.div`
  height: calc(100% - 108px);
  display: flex;
  align-items: center;
  justify-content: center;
`;

export default Notifications;
